/**
 * Cash Flow Page
 * Displays annual and monthly cash flow projections with charts and tables
 */

import { useEffect, useState } from 'react';
import { Card } from '@/components/common/Card';
import { useProjectStore } from '@/stores/projectStore';
import { Spinner, Button, Tab, TabList } from '@fluentui/react-components';
import { ArrowDownload24Regular } from '@fluentui/react-icons';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { formatCurrency } from '@/utils/formatters';
import { FINANCIAL_COLORS, CHART_COLORS } from '@/utils/chartColors';

export function CashFlow() {
  const { currentProject, currentCalculations, isLoading, fetchCalculations } =
    useProjectStore();
  const [viewMode, setViewMode] = useState<'annual' | 'monthly'>('annual');
  const [monthRange, setMonthRange] = useState<'all' | 'first24' | 'construction' | 'stabilized'>('first24');

  useEffect(() => {
    if (currentProject?.id) {
      fetchCalculations(currentProject.id);
    }
  }, [currentProject?.id, fetchCalculations]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="large" label="Loading cash flow data..." />
      </div>
    );
  }

  if (!currentCalculations) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card title="No Cash Flow Data">
          <p className="text-gray-90">
            Run calculations to see cash flow projections.
          </p>
        </Card>
      </div>
    );
  }

  const { annualCashFlows, monthlyCashFlows } = currentCalculations;

  // Prepare annual chart data
  const chartData = annualCashFlows.map((cf) => ({
    year: `Year ${cf.year}`,
    Revenue: cf.revenue / 1000000,
    Expenses: cf.expenses / 1000000,
    NOI: cf.noi / 1000000,
    'Debt Service': cf.debtService / 1000000,
    'Cash Flow': cf.cashFlow / 1000000,
  }));

  // Prepare cumulative cash flow data
  let cumulative = 0;
  const cumulativeData = annualCashFlows.map((cf) => {
    cumulative += cf.cashFlow;
    return {
      year: `Year ${cf.year}`,
      'Cumulative Cash Flow': cumulative / 1000000,
      'Annual Cash Flow': cf.cashFlow / 1000000,
    };
  });

  // Monthly data filtering based on range
  const getMonthlyData = () => {
    switch (monthRange) {
      case 'all':
        return monthlyCashFlows;
      case 'first24':
        return monthlyCashFlows.slice(0, 24);
      case 'construction':
        return monthlyCashFlows.slice(0, 18); // First 18 months
      case 'stabilized':
        return monthlyCashFlows.slice(30); // After month 30
      default:
        return monthlyCashFlows.slice(0, 24);
    }
  };

  const displayedMonthlyData = getMonthlyData();

  // Monthly chart data
  const monthlyChartData = displayedMonthlyData.map((cf) => ({
    month: `M${cf.month}`,
    Revenue: cf.revenue.effectiveRevenue / 1000000,
    Expenses: cf.expenses.totalExpenses / 1000000,
    NOI: cf.noi / 1000000,
    'Debt Service': cf.debtService / 1000000,
    'Cash Flow': cf.equityCashFlow / 1000000,
    Development: (cf.landCosts + cf.hardCosts + cf.softCosts) / 1000000,
  }));

  // Monthly cumulative
  let monthlyCumulative = 0;
  const monthlyCumulativeData = displayedMonthlyData.map((cf) => {
    monthlyCumulative += cf.equityCashFlow;
    return {
      month: `M${cf.month}`,
      'Cumulative CF': monthlyCumulative / 1000000,
      'Monthly CF': cf.equityCashFlow / 1000000,
    };
  });

  // Summary calculations based on view mode
  const summaryData = viewMode === 'annual'
    ? {
        totalRevenue: annualCashFlows.reduce((sum, cf) => sum + cf.revenue, 0),
        totalNOI: annualCashFlows.reduce((sum, cf) => sum + cf.noi, 0),
        totalDebtService: annualCashFlows.reduce((sum, cf) => sum + cf.debtService, 0),
        totalCashFlow: annualCashFlows.reduce((sum, cf) => sum + cf.cashFlow, 0),
      }
    : {
        totalRevenue: displayedMonthlyData.reduce((sum, cf) => sum + cf.revenue.effectiveRevenue, 0),
        totalNOI: displayedMonthlyData.reduce((sum, cf) => sum + cf.noi, 0),
        totalDebtService: displayedMonthlyData.reduce((sum, cf) => sum + cf.debtService, 0),
        totalCashFlow: displayedMonthlyData.reduce((sum, cf) => sum + cf.equityCashFlow, 0),
      };

  return (
    <div className="space-y-[35px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-[35px]">
        <div>
          <h1 className="text-[36px] font-light text-dark-text mb-2">
            Cash Flow Analysis
          </h1>
          <p className="text-gray-300 text-sm">
            {currentProject?.name} • {annualCashFlows.length} years • {monthlyCashFlows.length} months
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <TabList
            selectedValue={viewMode}
            onTabSelect={(_, data) => setViewMode(data.value as 'annual' | 'monthly')}
          >
            <Tab value="annual">Annual View</Tab>
            <Tab value="monthly">Monthly View</Tab>
          </TabList>
          <Button
            appearance="primary"
            icon={<ArrowDownload24Regular />}
            onClick={() => alert('Export feature coming soon!')}
          >
            Export
          </Button>
        </div>
      </div>

      {/* Month Range Selector (only for monthly view) */}
      {viewMode === 'monthly' && (
        <Card>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-300 font-medium">Time Period:</span>
            <TabList
              selectedValue={monthRange}
              onTabSelect={(_, data) => setMonthRange(data.value as any)}
            >
              <Tab value="first24">First 24 Months</Tab>
              <Tab value="construction">Construction (18mo)</Tab>
              <Tab value="stabilized">Stabilized (30+)</Tab>
              <Tab value="all">All Months ({monthlyCashFlows.length})</Tab>
            </TabList>
          </div>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-[30px]">
        <div className="bg-white p-[25px] rounded-lg shadow-minimal text-center">
          <p className="text-[11px] uppercase text-gray-400 tracking-[0.5px] mb-[10px] font-medium">
            Total Revenue
          </p>
          <p className="text-[24px] font-semibold text-dark-text">
            {formatCurrency(summaryData.totalRevenue)}
          </p>
          <p className="text-xs text-gray-300 mt-1">
            {viewMode === 'annual' ? `${annualCashFlows.length} years` : `${displayedMonthlyData.length} months`}
          </p>
        </div>
        <div className="bg-white p-[25px] rounded-lg shadow-minimal text-center">
          <p className="text-[11px] uppercase text-gray-400 tracking-[0.5px] mb-[10px] font-medium">
            Total NOI
          </p>
          <p className="text-[24px] font-semibold text-success-green">
            {formatCurrency(summaryData.totalNOI)}
          </p>
          <p className="text-xs text-gray-300 mt-1">Net Operating Income</p>
        </div>
        <div className="bg-white p-[25px] rounded-lg shadow-minimal text-center">
          <p className="text-[11px] uppercase text-gray-400 tracking-[0.5px] mb-[10px] font-medium">
            Total Debt Service
          </p>
          <p className="text-[24px] font-semibold text-error-red">
            {formatCurrency(summaryData.totalDebtService)}
          </p>
          <p className="text-xs text-gray-300 mt-1">Interest + Principal</p>
        </div>
        <div className="bg-white p-[25px] rounded-lg shadow-minimal text-center">
          <p className="text-[11px] uppercase text-gray-400 tracking-[0.5px] mb-[10px] font-medium">
            Total Cash Flow
          </p>
          <p className="text-[24px] font-semibold text-dark-text">
            {formatCurrency(summaryData.totalCashFlow)}
          </p>
          <p className="text-xs text-gray-300 mt-1">After Debt Service</p>
        </div>
      </div>

      {/* Annual View Charts */}
      {viewMode === 'annual' && (
        <>
          {/* Revenue & Expenses Chart */}
          <Card
            title="Revenue & Operating Performance"
            subtitle="Annual revenue, expenses, and NOI"
          >
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d0d0d0" vertical={false} />
                <XAxis
                  dataKey="year"
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                />
                <YAxis
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                  label={{
                    value: 'Amount ($M)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#999', fontSize: 11 },
                  }}
                />
                <Tooltip
                  formatter={(value: number) => `$${value.toFixed(2)}M`}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Revenue" fill={FINANCIAL_COLORS.revenue} radius={[2, 2, 0, 0]} />
                <Bar dataKey="Expenses" fill={FINANCIAL_COLORS.expenses} radius={[2, 2, 0, 0]} />
                <Bar dataKey="NOI" fill={FINANCIAL_COLORS.noi} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* Cash Flow Chart */}
          <Card
            title="Cash Flow After Debt Service"
            subtitle="Annual cash flow to equity"
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d0d0d0" vertical={false} />
                <XAxis
                  dataKey="year"
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                />
                <YAxis
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                  label={{
                    value: 'Cash Flow ($M)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#999', fontSize: 11 },
                  }}
                />
                <Tooltip
                  formatter={(value: number) => `$${value.toFixed(2)}M`}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="Cash Flow"
                  stroke={CHART_COLORS.purple}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="Debt Service"
                  stroke={FINANCIAL_COLORS.expenses}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* Cumulative Cash Flow Chart */}
          <Card
            title="Cumulative Cash Flow"
            subtitle="Total project cash flow over time"
          >
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={cumulativeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d0d0d0" vertical={false} />
                <XAxis
                  dataKey="year"
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                />
                <YAxis
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                  label={{
                    value: 'Amount ($M)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#999', fontSize: 11 },
                  }}
                />
                <Tooltip
                  formatter={(value: number) => `$${value.toFixed(2)}M`}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                <Area
                  type="monotone"
                  dataKey="Cumulative Cash Flow"
                  fill={CHART_COLORS.blue}
                  stroke={CHART_COLORS.blue}
                  fillOpacity={0.3}
                />
                <Bar dataKey="Annual Cash Flow" fill={FINANCIAL_COLORS.revenue} radius={[2, 2, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>

          {/* Stacked Area Chart */}
          <Card
            title="Revenue & Expense Components"
            subtitle="Stacked area visualization"
          >
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d0d0d0" vertical={false} />
                <XAxis
                  dataKey="year"
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                />
                <YAxis
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                  label={{
                    value: 'Amount ($M)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#999', fontSize: 11 },
                  }}
                />
                <Tooltip
                  formatter={(value: number) => `$${value.toFixed(2)}M`}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey="Revenue"
                  stackId="1"
                  stroke={FINANCIAL_COLORS.revenue}
                  fill={FINANCIAL_COLORS.revenue}
                  fillOpacity={0.6}
                />
                <Area
                  type="monotone"
                  dataKey="Expenses"
                  stackId="2"
                  stroke={FINANCIAL_COLORS.expenses}
                  fill={FINANCIAL_COLORS.expenses}
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}

      {/* Monthly View Charts */}
      {viewMode === 'monthly' && (
        <>
          {/* Monthly Revenue & NOI */}
          <Card
            title="Monthly Revenue & NOI"
            subtitle={`${displayedMonthlyData.length} months of operating performance`}
          >
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d0d0d0" vertical={false} />
                <XAxis
                  dataKey="month"
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                />
                <YAxis
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                  label={{
                    value: 'Amount ($M)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#999', fontSize: 11 },
                  }}
                />
                <Tooltip
                  formatter={(value: number) => `$${value.toFixed(3)}M`}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Revenue" fill={FINANCIAL_COLORS.revenue} radius={[2, 2, 0, 0]} />
                <Bar dataKey="Expenses" fill={FINANCIAL_COLORS.expenses} radius={[2, 2, 0, 0]} />
                <Line
                  type="monotone"
                  dataKey="NOI"
                  stroke={CHART_COLORS.blue}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>

          {/* Monthly Cumulative Cash Flow */}
          <Card
            title="Cumulative Monthly Cash Flow"
            subtitle="Running total of cash flow"
          >
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={monthlyCumulativeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d0d0d0" vertical={false} />
                <XAxis
                  dataKey="month"
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                />
                <YAxis
                  stroke="#999"
                  tick={{ fontSize: 11, fill: '#999' }}
                  axisLine={{ stroke: '#d0d0d0' }}
                  label={{
                    value: 'Amount ($M)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#999', fontSize: 11 },
                  }}
                />
                <Tooltip
                  formatter={(value: number) => `$${value.toFixed(3)}M`}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                <Area
                  type="monotone"
                  dataKey="Cumulative CF"
                  fill={CHART_COLORS.blue}
                  stroke={CHART_COLORS.blue}
                  fillOpacity={0.3}
                />
                <Bar dataKey="Monthly CF" fill={CHART_COLORS.purple} radius={[2, 2, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}

      {/* Data Table */}
      {viewMode === 'annual' ? (
        <Card title="Annual Cash Flow Detail" subtitle="Complete breakdown by year">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-20 border-b-2 border-dark-text">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    Year
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    Revenue
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    Expenses
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    NOI
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    Debt Service
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    Cash Flow
                  </th>
                </tr>
              </thead>
              <tbody>
                {annualCashFlows.map((cf) => (
                  <tr
                    key={cf.year}
                    className="border-b border-border-light hover:bg-gray-10"
                  >
                    <td className="px-4 py-3 font-medium text-dark-text">Year {cf.year}</td>
                    <td className="px-4 py-3 text-right text-dark-text">
                      {formatCurrency(cf.revenue)}
                    </td>
                    <td className="px-4 py-3 text-right text-error-red">
                      {formatCurrency(cf.expenses)}
                    </td>
                    <td className="px-4 py-3 text-right text-success-green font-medium">
                      {formatCurrency(cf.noi)}
                    </td>
                    <td className="px-4 py-3 text-right text-dark-text">
                      {formatCurrency(cf.debtService)}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-semibold ${
                        cf.cashFlow >= 0 ? 'text-success-green' : 'text-error-red'
                      }`}
                    >
                      {formatCurrency(cf.cashFlow)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-20 border-t-2 border-dark-text">
                <tr>
                  <td className="px-4 py-3 font-bold text-dark-text">TOTAL</td>
                  <td className="px-4 py-3 text-right font-bold text-dark-text">
                    {formatCurrency(
                      annualCashFlows.reduce((sum, cf) => sum + cf.revenue, 0)
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-error-red">
                    {formatCurrency(
                      annualCashFlows.reduce((sum, cf) => sum + cf.expenses, 0)
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-success-green">
                    {formatCurrency(
                      annualCashFlows.reduce((sum, cf) => sum + cf.noi, 0)
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-dark-text">
                    {formatCurrency(
                      annualCashFlows.reduce((sum, cf) => sum + cf.debtService, 0)
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-dark-text">
                    {formatCurrency(
                      annualCashFlows.reduce((sum, cf) => sum + cf.cashFlow, 0)
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      ) : (
        <Card title="Monthly Cash Flow Detail" subtitle={`${displayedMonthlyData.length} months of data`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-20 border-b-2 border-dark-text">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    Month
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    Revenue
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    Expenses
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    NOI
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    Development
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    Debt Service
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                    Cash Flow
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedMonthlyData.map((cf) => (
                  <tr
                    key={cf.month}
                    className="border-b border-border-light hover:bg-gray-10"
                  >
                    <td className="px-4 py-3 font-medium text-dark-text">Month {cf.month}</td>
                    <td className="px-4 py-3 text-right text-dark-text">
                      {formatCurrency(cf.revenue.effectiveRevenue)}
                    </td>
                    <td className="px-4 py-3 text-right text-error-red">
                      {formatCurrency(cf.expenses.totalExpenses)}
                    </td>
                    <td className="px-4 py-3 text-right text-success-green font-medium">
                      {formatCurrency(cf.noi)}
                    </td>
                    <td className="px-4 py-3 text-right text-dark-text">
                      {formatCurrency(cf.landCosts + cf.hardCosts + cf.softCosts)}
                    </td>
                    <td className="px-4 py-3 text-right text-dark-text">
                      {formatCurrency(cf.debtService)}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-semibold ${
                        cf.equityCashFlow >= 0 ? 'text-success-green' : 'text-error-red'
                      }`}
                    >
                      {formatCurrency(cf.equityCashFlow)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-20 border-t-2 border-dark-text">
                <tr>
                  <td className="px-4 py-3 font-bold text-dark-text">TOTAL</td>
                  <td className="px-4 py-3 text-right font-bold text-dark-text">
                    {formatCurrency(summaryData.totalRevenue)}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-error-red">
                    {formatCurrency(
                      displayedMonthlyData.reduce((sum, cf) => sum + cf.expenses.totalExpenses, 0)
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-success-green">
                    {formatCurrency(summaryData.totalNOI)}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-dark-text">
                    {formatCurrency(
                      displayedMonthlyData.reduce((sum, cf) => sum + cf.landCosts + cf.hardCosts + cf.softCosts, 0)
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-dark-text">
                    {formatCurrency(summaryData.totalDebtService)}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-dark-text">
                    {formatCurrency(summaryData.totalCashFlow)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
